import React from "react";
import { PromptEventStore } from "@/app_store/agent/PromptEvent.generated";

// ── Public types ──────────────────────────────────────────────────────────────

export interface ToolWidgetProps {
    item: PromptEventStore;
    index: number;
    isExpanded: boolean;
    toggleExpanded: (index: number) => void;
    onOpenInCode?: (filePath: string) => void;
    onOpenInReview?: (filePath: string) => void;
}

export type ToolWidgetComponent = React.ComponentType<ToolWidgetProps>;

// ── Registry ──────────────────────────────────────────────────────────────────

const widgetRegistry = new Map<string, ToolWidgetComponent>();

export function registerWidget(toolName: string, widget: ToolWidgetComponent): void {
    widgetRegistry.set(toolName, widget);
}

export function resolveWidget(toolName: string | undefined): ToolWidgetComponent {
    if (toolName) {
        const w = widgetRegistry.get(toolName);
        if (w) return w;
    }
    return DefaultToolWidget;
}

// ── Default widget (fallback — current collapsible pre block) ─────────────────

export const DefaultToolWidget: ToolWidgetComponent = ({ item, index, isExpanded, toggleExpanded }) => {
    const isCollapsible = item.content.length > 150;
    return (
        <div
            className={`agent_pane__item_content${isCollapsible ? " agent_pane__item_content--collapsible" : ""}${isCollapsible && !isExpanded ? " agent_pane__item_content--collapsed" : ""}`}
            onClick={isCollapsible ? () => toggleExpanded(index) : undefined}
        >
            <pre className="agent_pane__item_pre">{item.content}</pre>
            {isCollapsible && !isExpanded && (
                <div className="agent_pane__expand_hint">···</div>
            )}
        </div>
    );
};

// ── Read widget ───────────────────────────────────────────────────────────────

const ReadWidget: ToolWidgetComponent = ({ item, onOpenInCode }) => {
    const inp = item.tool_input as { file_path?: string; offset?: number; limit?: number } | undefined;
    const filePath = inp?.file_path ?? item.content;
    const rangeStart = inp?.offset !== undefined ? inp.offset + 1 : null;
    const rangeEnd = rangeStart !== null && inp?.limit !== undefined ? rangeStart + inp.limit - 1 : null;

    return (
        <div
            className={`agent_pane__item_content agent_pane__tool_widget agent_pane__tool_widget--read${onOpenInCode ? " agent_pane__tool_widget--clickable" : ""}`}
            onClick={onOpenInCode ? () => onOpenInCode(filePath) : undefined}
        >
            <span className="agent_pane__tool_widget_label">read</span>
            <span className="agent_pane__tool_widget_path">{filePath}</span>
            {rangeStart !== null && (
                <span className="agent_pane__tool_widget_range">
                    :{rangeStart}{rangeEnd !== null ? `–${rangeEnd}` : "+"}
                </span>
            )}
        </div>
    );
};

// ── Edit widget ───────────────────────────────────────────────────────────────

const EditWidget: ToolWidgetComponent = ({ item, onOpenInReview }) => {
    const inp = item.tool_input as { file_path?: string } | undefined;
    const filePath = inp?.file_path ?? item.content;

    return (
        <div
            className={`agent_pane__item_content agent_pane__tool_widget agent_pane__tool_widget--edit${onOpenInReview ? " agent_pane__tool_widget--clickable" : ""}`}
            onClick={onOpenInReview ? () => onOpenInReview(filePath) : undefined}
        >
            <span className="agent_pane__tool_widget_label">edit</span>
            <span className="agent_pane__tool_widget_path">{filePath}</span>
        </div>
    );
};

// ── Write widget ──────────────────────────────────────────────────────────────

const WriteWidget: ToolWidgetComponent = ({ item, onOpenInReview }) => {
    const inp = item.tool_input as { file_path?: string } | undefined;
    const filePath = inp?.file_path ?? item.content;

    return (
        <div
            className={`agent_pane__item_content agent_pane__tool_widget agent_pane__tool_widget--write${onOpenInReview ? " agent_pane__tool_widget--clickable" : ""}`}
            onClick={onOpenInReview ? () => onOpenInReview(filePath) : undefined}
        >
            <span className="agent_pane__tool_widget_label">write</span>
            <span className="agent_pane__tool_widget_path">{filePath}</span>
        </div>
    );
};

// ── Bash widget ───────────────────────────────────────────────────────────────

const BashWidget: ToolWidgetComponent = ({ item, index, isExpanded, toggleExpanded }) => {
    const inp = item.tool_input as { command?: string; description?: string } | undefined;
    const command = inp?.command ?? item.command ?? item.content;
    const description = inp?.description;
    const isCollapsible = command.length > 120;

    return (
        <div
            className={`agent_pane__item_content agent_pane__tool_widget agent_pane__tool_widget--bash${isCollapsible ? " agent_pane__item_content--collapsible" : ""}${isCollapsible && !isExpanded ? " agent_pane__item_content--collapsed" : ""}`}
            onClick={isCollapsible ? () => toggleExpanded(index) : undefined}
        >
            {description && (
                <span className="agent_pane__tool_widget_desc">{description}</span>
            )}
            <pre className="agent_pane__tool_widget_cmd">
                <span className="agent_pane__tool_widget_prompt">$</span>{" "}{command}
            </pre>
            {isCollapsible && !isExpanded && (
                <div className="agent_pane__expand_hint">···</div>
            )}
        </div>
    );
};

// ── Grep widget ───────────────────────────────────────────────────────────────

const GrepWidget: ToolWidgetComponent = ({ item }) => {
    const inp = item.tool_input as { pattern?: string; path?: string; glob?: string } | undefined;
    const pattern = inp?.pattern ?? item.content;
    const inPath = inp?.path;
    const glob = inp?.glob;

    return (
        <div className="agent_pane__item_content agent_pane__tool_widget agent_pane__tool_widget--grep">
            <span className="agent_pane__tool_widget_label">grep</span>
            <code className="agent_pane__tool_widget_path">{pattern}</code>
            {inPath && <span className="agent_pane__tool_widget_meta">{inPath}</span>}
            {glob && <span className="agent_pane__tool_widget_meta">{glob}</span>}
        </div>
    );
};

// ── Glob widget ───────────────────────────────────────────────────────────────

const GlobWidget: ToolWidgetComponent = ({ item }) => {
    const inp = item.tool_input as { pattern?: string; path?: string } | undefined;
    const pattern = inp?.pattern ?? item.content;
    const inPath = inp?.path;

    return (
        <div className="agent_pane__item_content agent_pane__tool_widget agent_pane__tool_widget--glob">
            <span className="agent_pane__tool_widget_label">glob</span>
            <code className="agent_pane__tool_widget_path">{pattern}</code>
            {inPath && <span className="agent_pane__tool_widget_meta">{inPath}</span>}
        </div>
    );
};

// ── Register built-in widgets ─────────────────────────────────────────────────

registerWidget("Read", ReadWidget);
registerWidget("Edit", EditWidget);
registerWidget("Write", WriteWidget);
registerWidget("Bash", BashWidget);
registerWidget("Grep", GrepWidget);
registerWidget("Glob", GlobWidget);
